import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Empty, List, Progress, Skeleton, Space, Tag, Typography } from "antd";
import {
  IdcardOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { profileCompletion, profileTitle, toList } from "./professionalProfileUtils";
import "./OlreadiaFlowPages.css";

const { Paragraph, Text, Title } = Typography;

export default function ProfessionalProfiles() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadProfiles = useCallback(async () => {
    if (!actorId) return;
    try {
      setLoading(true);
      setError("");
      const result = await olreadiaService.professionProfiles.listForUser(actorId, {
        actorId,
        workspaceId: selectedWorkspaceId,
        page: 0,
        size: 50,
      });
      setProfiles(toList(result));
    } catch (nextError) {
      setError(nextError?.message || "Could not load professional profiles.");
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [actorId, selectedWorkspaceId]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const renderProfileCard = (profile, icon = <IdcardOutlined />) => (
    <Card
      className="olreadia-flow-card olreadia-flow-profile-card"
      hoverable
      onClick={() => profile.id && navigate(paths.professionalProfileDetails(profile.id))}
    >
      <div className="olreadia-flow-card__top">
        <span className="olreadia-flow-card__icon">{icon}</span>
        <div>
          <Title level={4}>{profileTitle(profile)}</Title>
          <Space wrap size={6} className="olreadia-flow-profile-card__meta">
            <Tag>{profile.educationLevel || "Education not set"}</Tag>
            <Tag>{toList(profile.interestedServices).length} services</Tag>
            <Tag>{toList(profile.interestedLocations).length} locations</Tag>
            <Tag>{toList(profile.references).length} references</Tag>
          </Space>
        </div>
        <Tag className="olreadia-flow-status is-active">{profileCompletion(profile)}%</Tag>
      </div>
      <div className="olreadia-flow-profile-card__footer" onClick={(event) => event.stopPropagation()}>
        <Progress percent={profileCompletion(profile)} showInfo={false} strokeColor="#27ae30" trailColor="#e8f8ea" />
        <Space wrap size={6}>
          <Button size="small" type="primary" onClick={() => profile.id && navigate(paths.professionalProfileDetails(profile.id))}>
            Open
          </Button>
          <Button size="small" icon={<SafetyCertificateOutlined />} onClick={() => navigate(paths.backgroundChecks)}>
            Checks
          </Button>
        </Space>
      </div>
    </Card>
  );

  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout olreadia-flow-layout--full">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon"><IdcardOutlined /></span>
            <div>
              <Text className="olreadia-flow-eyebrow">Professional profiles</Text>
              <Title level={2}>Manage Professional Profiles</Title>
              <Paragraph>
                Build and manage your professional profile for roles, services, locations, work history, documents, and references.
              </Paragraph>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={loadProfiles} loading={loading}>Refresh</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(paths.professionalProfileCreate)}>
                Create Professional Profile
              </Button>
            </Space>
          </section>

          {error ? (
            <Alert
              type="error"
              showIcon
              message="Unable to load professional profiles"
              description={error}
              action={<Button onClick={loadProfiles}>Retry</Button>}
              style={{ marginBottom: 12 }}
            />
          ) : null}

          {loading && !profiles.length ? (
            <Card className="olreadia-flow-card"><Skeleton active paragraph={{ rows: 4 }} /></Card>
          ) : (
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <Card
                className="olreadia-flow-card olreadia-flow-profile-card olreadia-flow-add-card"
                hoverable
                onClick={() => navigate(paths.professionalProfileCreate)}
              >
                <div className="olreadia-flow-card__top">
                  <span className="olreadia-flow-card__icon"><IdcardOutlined /></span>
                  <div>
                    <Title level={4}>Create Professional Profile</Title>
                    <Text className="olreadia-flow-profile-card__summary">Create or update one professional profile for your skills, services, work history, and documents.</Text>
                  </div>
                  <PlusOutlined />
                </div>
              </Card>

              {profiles.length ? (
                <List
                  className="olreadia-flow-list"
                  dataSource={profiles}
                  renderItem={(profile) => (
                    <List.Item className="olreadia-flow-list-item">
                      {renderProfileCard(profile)}
                    </List.Item>
                  )}
                />
              ) : (
                <Card className="olreadia-flow-card">
                  <Empty description="No professional profiles yet">
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(paths.professionalProfileCreate)}>
                      Create Professional Profile
                    </Button>
                  </Empty>
                </Card>
              )}
            </Space>
          )}
        </main>
      </div>
    </div>
  );
}
